import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { isMerchCartType, offsiteCartItemMatches } from "../../utils/cartArtUtils";

export interface CartItem {
    id: string | number;
    name: string;
    type: string;
    quantity: number;
    price: number | string;
    sku_code?: string;
    imageUrl?: string;
    finalCanvasImage?: any;
    imgURL?: string;
    size?: string;
    dimensions?: string;
    inches?: string;
    variety?: string;
    color?: string;
    productId?: string | number;
    productSizeId?: string | number;
    artworkPlacement?: unknown;
    isPack?: boolean;
    /** Included products when this cart line is a pack. */
    products?: Array<Record<string, any>>;
}

type OffsiteCartPayload = {
    id: string | number;
    type: string;
    size?: string;
    variety?: string;
    color?: string;
    finalCanvasImage?: unknown;
};

interface CartState {
    items: CartItem[];
}

const initialState: CartState = {
    items: [],
};

function findOffsiteCartItem(state: CartState, payload: OffsiteCartPayload) {
    return state.items.find((item) => offsiteCartItemMatches(item, payload));
}

const cartSlice = createSlice({
    name: "cart",
    initialState,
    reducers: {
        addToCart: (state, action: PayloadAction<CartItem>) => {
            const payload = action.payload;

            if (isMerchCartType(payload.type)) {
                const existingItem = findOffsiteCartItem(state, payload);

                if (existingItem) {
                    existingItem.quantity += payload.quantity || 1;
                } else {
                    state.items.push({
                        ...payload,
                        productId: payload.productId || payload.id,
                        quantity: payload.quantity || 1,
                    });
                }
            } else {
                const existingItem = state.items.find(
                    (item) => item.id === payload.id && item.type === payload.type
                );

                if (existingItem) {
                    existingItem.quantity += payload.quantity || 1;
                } else {
                    state.items.push({
                        ...payload,
                        quantity: payload.quantity || 1,
                    });
                }
            }
        },
        /** Set cart line quantity (add if missing). Use when confirming qty from modal/card. */
        upsertCartItem: (state, action: PayloadAction<CartItem>) => {
            const payload = action.payload;
            const quantity = Math.max(1, payload.quantity || 1);

            if (isMerchCartType(payload.type)) {
                const existingItem = findOffsiteCartItem(state, payload);

                if (existingItem) {
                    existingItem.quantity = quantity;
                    if (payload.products) existingItem.products = payload.products;
                    if (payload.isPack !== undefined) existingItem.isPack = payload.isPack;
                    if (payload.price !== undefined) existingItem.price = payload.price;
                    if (payload.imageUrl) existingItem.imageUrl = payload.imageUrl;
                    if (payload.imgURL) existingItem.imgURL = payload.imgURL;
                    if (payload.size !== undefined) existingItem.size = payload.size;
                    if (payload.dimensions !== undefined) existingItem.dimensions = payload.dimensions;
                    if (payload.variety !== undefined) existingItem.variety = payload.variety;
                    if (payload.color !== undefined) existingItem.color = payload.color;
                    if (payload.productSizeId !== undefined) {
                        existingItem.productSizeId = payload.productSizeId;
                    }
                    if (payload.finalCanvasImage !== undefined) {
                        existingItem.finalCanvasImage = payload.finalCanvasImage;
                    }
                } else {
                    state.items.push({
                        ...payload,
                        productId: payload.productId || payload.id,
                        quantity,
                    });
                }
            } else {
                const existingItem = state.items.find(
                    (item) => item.id === payload.id && item.type === payload.type
                );

                if (existingItem) {
                    existingItem.quantity = quantity;
                } else {
                    state.items.push({
                        ...payload,
                        quantity,
                    });
                }
            }
        },
        updateCartItemQuantity: (state, action: PayloadAction<OffsiteCartPayload & { quantity: number }>) => {
            const { id, type, quantity, size, variety, color, finalCanvasImage } = action.payload;
            let item;

            if (isMerchCartType(type)) {
                item = findOffsiteCartItem(state, { id, type, size, variety, color, finalCanvasImage });
            } else {
                item = state.items.find(
                    (item) => item.id === id && item.type === type
                );
            }

            if (item) {
                item.quantity = Math.max(1, quantity);
            }
        },
        removeFromCart: (state, action: PayloadAction<OffsiteCartPayload>) => {
            const { id, type, size, variety, color, finalCanvasImage } = action.payload;

            if (isMerchCartType(type)) {
                state.items = state.items.filter(
                    (item) => !offsiteCartItemMatches(item, { id, type, size, variety, color, finalCanvasImage })
                );
            } else {
                state.items = state.items.filter(
                    (item) => !(item.id === id && item.type === type)
                );
            }
        },
        clearCart: (state) => {
            state.items = [];
        },
        incrementQuantity: (state, action: PayloadAction<OffsiteCartPayload>) => {
            const { id, type, size, variety, color, finalCanvasImage } = action.payload;
            let item;

            if (isMerchCartType(type)) {
                item = findOffsiteCartItem(state, { id, type, size, variety, color, finalCanvasImage });
            } else {
                item = state.items.find(
                    (item) => item.id === id && item.type === type
                );
            }

            if (item) {
                item.quantity += 1;
            }
        },
        decrementQuantity: (state, action: PayloadAction<OffsiteCartPayload>) => {
            const { id, type, size, variety, color, finalCanvasImage } = action.payload;
            let item;

            if (isMerchCartType(type)) {
                item = findOffsiteCartItem(state, { id, type, size, variety, color, finalCanvasImage });
            } else {
                item = state.items.find(
                    (item) => item.id === id && item.type === type
                );
            }

            if (item && item.quantity > 1) {
                item.quantity -= 1;
            }
        },
    },
});

export const {
    addToCart,
    upsertCartItem,
    updateCartItemQuantity,
    removeFromCart,
    clearCart,
    incrementQuantity,
    decrementQuantity,
} = cartSlice.actions;

export default cartSlice.reducer;
